import { cn } from '@forest/shared'
import { Dialog } from '@kobalte/core/dialog'
import type { JSXElement } from 'solid-js'
import { createStore } from 'solid-js/store'
import bannerImg from '~/assets/images/error-dialog-banner.png'
import { Spinner } from '~/components/ui/spinner'

const [errorDialog, setErrorDialog] = createStore<{
  isOpen: boolean
  isLoading: boolean
  title: string
  description: string | JSXElement
  buttonText: string
  buttonAction?: () => void
  asyncAction?: () => Promise<void>
}>({
  isOpen: false,
  isLoading: false,
  title: 'TITLE',
  description: 'CONTENT',
  buttonText: 'BUTTON',
})

export const closeErrorDialog = () => {
  setErrorDialog({ isOpen: false, isLoading: false })
}

export const showErrorDialog = (payload: {
  title: string
  description: string | JSXElement
  buttonText: string
  buttonAction?: () => void
  asyncAction?: () => Promise<void>
}) => {
  setErrorDialog({
    isOpen: true,
    isLoading: false,
    buttonAction: undefined,
    asyncAction: undefined,
    ...payload,
  })
}

export const setErrorDialogLoading = (isLoading: boolean) => {
  setErrorDialog('isLoading', isLoading)
}

export function ErrorDialog() {
  const handleButtonClick = () => {
    if (errorDialog.asyncAction) {
      errorDialog.asyncAction()
    } else {
      errorDialog.buttonAction?.()
      closeErrorDialog()
    }
  }

  return (
    <Dialog
      open={errorDialog.isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen && !errorDialog.isLoading) closeErrorDialog()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          class={cn(
            'data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0 peer fixed inset-0 z-[100] bg-[rgba(19,19,38,0.30)] backdrop-blur-md',
          )}
        />
        <Dialog.Content
          class="fixed left-1/2 top-1/2 z-[100] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-[8px] bg-white data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0"
          onInteractOutside={(e) => {
            e.preventDefault()
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault()
          }}
          style={{
            '--tw-enter-translate-x': '-50%',
            '--tw-enter-translate-y': '-50%',
            '--tw-exit-translate-x': '-50%',
            '--tw-exit-translate-y': '-50%',
          }}
        >
          <div class="h-[140px] w-full">
            <img src={bannerImg} />
          </div>
          <div class="px-5 pb-5 text-center">
            <Dialog.Title class="text-lg text-[#333333]">
              {errorDialog.title}
            </Dialog.Title>

            <div class="mt-3 text-center text-sm text-[#666666]">
              <p class="whitespace-pre-line break-words">
                {errorDialog.description}
              </p>
            </div>

            <div class="mt-4">
              <button
                class="relative mx-auto flex h-9 w-full max-w-[160px] items-center justify-center rounded-[5px] bg-[#8DC926] font-bold text-white shadow-[0_4px_0_#63A408] active:translate-y-[4px] active:shadow-[0_0px_0_#63A408] disabled:bg-[#639897] disabled:shadow-[0_4px_0_#4a7a79]"
                disabled={errorDialog.isLoading}
                onClick={handleButtonClick}
              >
                {errorDialog.isLoading && (
                  <Spinner class="absolute right-7 text-[#176361]" />
                )}
                {errorDialog.buttonText}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
